/**
 * Правка учёбки с доски ОО (13.10, владелец: «учёбкам в расстановке у ОО нужно иметь
 * возможность отредактировать»). Окно — oo/StudyEditSheet.tsx; здесь план запросов и
 * исполнитель. Учёбка — это строки extra_work одного слота (ученики + куратор), поэтому
 * правка = по запросу на строку, а смена куратора = снять старого и назначить нового.
 *
 * Порядок (13.10): снять старого куратора → поправить строки → назначить нового.
 * Снятие ПЕРВЫМ: сервер переписывает ученикам сообщение по их ТЕКУЩЕМУ слоту — снимем
 * куратора после переноса, и строка «👤 Куратор» у них останется старой. Снятие не прошло —
 * дальше не идём: иначе в учёбке окажутся два куратора.
 * Ставка куратора едет в том же PUT, что и его время/тема: одно изменение — одна правка
 * сообщения в Telegram. Ученику ставку не шлём — сервер ответит 400 (учёбка не оплачивается).
 * Дату не правим (решение 13.10): перенос на другой день — отменить и создать заново.
 *
 * Модуль без импорта api — исполнитель получает вызов снаружи, план проверяется тестом
 * (studyEdit.test.ts) под node без браузера.
 */
import type { ExtraWorkGroup } from "./useExtraWork";

export interface StudyForm {
  timeFrom: string;
  timeTo: string;
  description: string;
  /** null — «любой» филиал. */
  branchId: string | null;
  /** "" — без куратора. */
  curatorId: string;
  /** ₽/ч куратора; null — не задана (новый куратор получит свою ставку из справочника). */
  curatorRate: number | null;
}

export interface StudyReq {
  method: "PUT" | "POST" | "DELETE";
  path: string;
  body?: Record<string, unknown>;
  /** Чья строка — для итога «не выполнено для …». */
  who: string;
}

export interface StudyPlan {
  /** Снять прежнего куратора (с «Учёбка отменена», если учёбка отправлена). */
  drop: StudyReq | null;
  /** Правки строк — параллельно. */
  puts: StudyReq[];
  /** Назначить нового куратора — уже в новый слот. */
  add: StudyReq | null;
}

const curatorOf = (g: ExtraWorkGroup) => g.ops.find((o) => o.kind === "training_curator") ?? null;

/** Начальное состояние окна — как карточка сейчас. */
export function studyFormOf(g: ExtraWorkGroup): StudyForm {
  const cur = curatorOf(g);
  return { timeFrom: g.timeFrom, timeTo: g.timeTo, description: g.description, branchId: g.branchId,
    curatorId: cur?.operatorId ?? "", curatorRate: cur ? cur.rate : null };
}

export const planIsEmpty = (p: StudyPlan): boolean => !p.drop && !p.add && p.puts.length === 0;

type Slot = { timeFrom: string; timeTo: string; branch: string | null; desc: string };
const slotOfForm = (f: StudyForm): Slot => ({ timeFrom: f.timeFrom, timeTo: f.timeTo, branch: f.branchId || null, desc: f.description.trim() });
/** Другие карточки учёбки того же слота: сохранённая и отправленная части одной учёбки — разные карточки,
 *  а сервер связывает куратора с учениками по слоту, не глядя на черновик. */
const twinsOf = (g: ExtraWorkGroup, groups: ExtraWorkGroup[], s: Slot) => groups.filter((x) => x.training && x.key !== g.key
  && x.timeFrom === s.timeFrom && x.timeTo === s.timeTo && (x.branchId || null) === s.branch && x.description === s.desc);

/** Кого нельзя выбрать куратором: ученики этой учёбки и все из соседней карточки того же слота
 *  (13.10: иначе один человек — и ученик, и куратор, или два оплачиваемых куратора на одну учёбку). */
export function notCurator(g: ExtraWorkGroup, groups: ExtraWorkGroup[], f: StudyForm): Set<string> {
  return new Set([...g.ops.filter((o) => o.kind === "training"), ...twinsOf(g, groups, slotOfForm(f)).flatMap((x) => x.ops)].map((o) => o.operatorId));
}

/** Выбор куратора в окне. 13.10: ставки личные (грейды) — новый куратор получает свою ставку из справочника,
 *  а не ставку прежнего; вернули прежнего — его ставка. Набранную в окне ставку ОО не сбрасываем. */
export function withCurator(f: StudyForm, g: ExtraWorkGroup, id: string, rateTyped: boolean): StudyForm {
  const old = curatorOf(g);
  return { ...f, curatorId: id, curatorRate: rateTyped ? f.curatorRate : old && old.operatorId === id ? old.rate : null };
}

/** Чистый план: только изменённые поля; ошибка — текстом для окна. */
export function planStudyEdit(g: ExtraWorkGroup, f: StudyForm, ctx: {
  date: string; groups: ExtraWorkGroup[]; nameOf?: (id: string) => string;
}): StudyPlan | { error: string } {
  const desc = f.description.trim();
  if (!/^\d{2}:\d{2}$/.test(f.timeFrom) || !/^\d{2}:\d{2}$/.test(f.timeTo) || f.timeFrom === f.timeTo) return { error: "Выберите время учёбки" };
  if (!desc) return { error: "Тема учёбки не может быть пустой" };
  const branch = f.branchId || null;

  const patch: Record<string, unknown> = {};
  if (f.timeFrom !== g.timeFrom) patch.time_from = f.timeFrom;
  if (f.timeTo !== g.timeTo) patch.time_to = f.timeTo;
  if (desc !== g.description) patch.description = desc;
  if (branch !== (g.branchId || null)) patch.branch_id = branch;
  const slotChanged = Object.keys(patch).length > 0;
  const twins = twinsOf(g, ctx.groups, slotOfForm(f));
  /* ученик и куратор связаны слотом (дата+время+филиал+тема): совпадёт с другой учёбкой —
     карточки и кураторы склеятся */
  if (slotChanged && twins.length) {
    return { error: "Такая учёбка уже есть — время, филиал и тема совпадают с другой карточкой" };
  }

  const old = curatorOf(g);
  const newId = f.curatorId;
  const curChanged = (old?.operatorId ?? "") !== newId;
  const twinOps = twins.flatMap((x) => x.ops);
  if (newId && [...g.ops, ...(curChanged ? twinOps : [])].some((o) => o.kind === "training" && o.operatorId === newId)) {
    return { error: "Куратор не может быть учеником этой учёбки" };
  }
  /* 13.10: соседняя карточка того же слота (сохранено/отправлено) уже с куратором — второй куратор
     той же учёбки получил бы вторую оплату */
  const twinCur = curChanged && newId ? twinOps.find((o) => o.kind === "training_curator") : undefined;
  if (twinCur) return { error: `У этой учёбки уже есть куратор — ${twinCur.operatorName} (в соседней карточке того же времени)` };
  const rate = f.curatorRate;
  const rateChanged = !!old && !curChanged && rate != null && rate !== old.rate;

  const drop: StudyReq | null = curChanged && old
    ? { method: "DELETE", path: `/api/extra-work/${old.rowId}?notify=cancel`, who: old.operatorName } : null;
  const puts: StudyReq[] = [];
  for (const o of g.ops) {
    if (drop && o.rowId === old?.rowId) continue;   // строка уходит целиком — править её незачем
    const body = { ...patch, ...(rateChanged && o.rowId === old?.rowId ? { hourly_rate: rate, rate_override: null } : {}) };
    if (Object.keys(body).length) puts.push({ method: "PUT", path: `/api/extra-work/${o.rowId}`, body, who: o.operatorName });
  }
  const add: StudyReq | null = curChanged && newId ? {
    method: "POST", path: "/api/extra-work", who: ctx.nameOf?.(newId) ?? newId,
    body: { kind: "training_curator", operator_id: newId, date: ctx.date, time_from: f.timeFrom, time_to: f.timeTo, description: desc,
      ...(branch ? { branch_id: branch } : {}), ...(rate != null ? { hourly_rate: rate } : {}), ...(g.draft ? { notify: false } : {}) },
  } : null;
  return { drop, puts, add };
}

/** Вызов API (на доске — api из api/client). */
export type StudyCall = (path: string, opts: { method: string; body?: unknown }) => Promise<unknown>;

const errText = (e: unknown) => (e instanceof Error ? e.message : "ошибка");
/** Сервер на чужую/пропавшую строку отвечает 200 с { error } — это тоже отказ. */
const replyError = (r: unknown): string | null =>
  r && typeof r === "object" && "error" in r ? String((r as { error: unknown }).error) : null;
/** false — сервер пытался написать и не смог (у черновика null: ему не пишут). */
const notified = (r: unknown): boolean | null =>
  r && typeof r === "object" && "notified" in r ? ((r as { notified: unknown }).notified as boolean | null) : null;

async function one(call: StudyCall, q: StudyReq): Promise<{ r: unknown; err: string | null }> {
  try {
    const r = await call(q.path, { method: q.method, ...(q.body ? { body: q.body } : {}) });
    return { r, err: replyError(r) };
  } catch (e) { return { r: null, err: errText(e) }; }
}

/** Исполнить план. Итог — строкой для плашки доски (showEwInfo): «не выполнена» / «НЕ доставлено» висят, пока не закроют. */
export async function runStudyEdit(plan: StudyPlan, call: StudyCall): Promise<{ ok: boolean; msg: string }> {
  const silent: string[] = [];
  if (plan.drop) {
    const d = await one(call, plan.drop);
    if (d.err) return { ok: false, msg: `Учёбка: правка не выполнена — не удалось снять куратора ${plan.drop.who} (${d.err})` };
    if (notified(d.r) === false) silent.push(`${plan.drop.who} (отмена)`);
  }
  const res = await Promise.all(plan.puts.map((q) => one(call, q).then((x) => ({ q, err: x.err }))));
  const failed = res.filter((x) => x.err).map((x) => `${x.q.who} (${x.err})`);
  if (plan.add) {
    const a = await one(call, plan.add);
    if (a.err) failed.push(`куратор ${plan.add.who} не назначен (${a.err})`);
    else if (notified(a.r) === false) silent.push(`${plan.add.who} (куратор)`);
  }
  if (failed.length) return { ok: false, msg: `Учёбка: правка не выполнена для ${failed.join(", ")}` };
  return { ok: true, msg: `Учёбка изменена${silent.length ? ` · НЕ доставлено: ${silent.join(", ")}` : ""}` };
}
